import { Drawer, type DrawerProps, Row, Space, Grid } from 'antd';
import { useFormikContext } from 'formik';
import { isNil, uniqBy } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { type MobilityStockPublicListFragment } from '../../../../api';
import { useListMobilityStocksQuery } from '../../../../api/queries/listMobilityStocks';
import { InventoryKind } from '../../../../api/types';
import Form from '../../../../components/fields/Form';
import PortalLoadingElement from '../../../../components/PortalLoadingElement';
import { useThemeComponents } from '../../../../themes';
import useTranslatedString, { type TranslatedString } from '../../../../utilities/useTranslatedString';
import { type GiftVoucherKYCJourneyValues } from './KYCPage/shared';
import StockCarCard from './StockCarCard';

type ChangeVehicleProps = Pick<DrawerProps, 'title' | 'visible' | 'onClose'> & {
    moduleId: string;
    translated: TranslatedString;
    onStockChange: (stock: MobilityStockPublicListFragment) => void;
};

type VehicleSelectionProps = {
    stocks: MobilityStockPublicListFragment[];
    translated: TranslatedString;
    selectedStockId: string;
    setSelectedStockId: (stockId: string) => void;
};

const StyledDrawer = styled(Drawer)`
    .ant-drawer-content-wrapper {
        max-width: 100%;
    }

    .ant-drawer-close {
        order: 2;
        margin-right: -12px;
    }

    .ant-drawer-footer {
        padding: 10px 24px;
    }
`;

const DrawerTitle = styled.p`
    font-weight: bold;
    font-size: 24px;
    margin: 0;
`;

const StyledForm = styled(Form)`
    label {
        font-weight: bold;
    }
`;

const VehicleSelection = ({ stocks, translated, setSelectedStockId, selectedStockId }: VehicleSelectionProps) => {
    const {
        FormFields: { SelectField },
    } = useThemeComponents();
    const { t } = useTranslation('giftVoucherJourney');

    const { values, setFieldValue } = useFormikContext<GiftVoucherKYCJourneyValues>();

    const inventoryDetails = useMemo(() => {
        const result = stocks.find(
            stock =>
                stock.id === selectedStockId &&
                stock.__typename === 'MobilityStockInventory' &&
                stock.inventory.__typename === 'MobilityInventory'
        );

        if (result?.__typename !== 'MobilityStockInventory') {
            return null;
        }

        if (result?.inventory?.__typename !== 'MobilityInventory') {
            return null;
        }

        return result.inventory;
    }, [stocks, selectedStockId]);

    const models = useMemo(
        () =>
            uniqBy(
                'id',
                stocks.map(
                    stock =>
                        stock.__typename === 'MobilityStockInventory' &&
                        stock.inventory.__typename === 'MobilityInventory' &&
                        stock.inventory.model
                )
            ).map(model => ({ label: translated(model.name), value: model.id })),
        [stocks, translated]
    );

    const variants = useMemo(
        () =>
            uniqBy(
                'id',
                stocks.map(
                    stock =>
                        stock.__typename === 'MobilityStockInventory' &&
                        stock.inventory.__typename === 'MobilityInventory' &&
                        !isNil(values.modelId) &&
                        stock.inventory.model.id === values.modelId &&
                        stock.inventory
                )
            )
                .filter(Boolean)
                .map(inventory => ({ label: translated(inventory.variant.name), value: inventory.variantSuiteId })),
        [stocks, translated, values.modelId]
    );

    const stockVariants = useMemo(
        () =>
            stocks
                .filter(
                    stock =>
                        stock.__typename === 'MobilityStockInventory' &&
                        stock.inventory.__typename === 'MobilityInventory' &&
                        stock.inventory.variantSuiteId === values.variantSuiteId
                )
                .filter(Boolean),
        [stocks, values.variantSuiteId]
    );

    // Initialize model id, if empty
    useEffect(() => {
        if (isNil(values.modelId) && models.length > 0) {
            setFieldValue('modelId', inventoryDetails?.model?.id ?? models[0].value);
        }
    }, [inventoryDetails?.model?.id, models, setFieldValue, values.modelId]);

    // On model change, set variant to first from option
    // Should be triggered also because of above effect
    const previousModelId = useRef(values.modelId);
    useEffect(() => {
        if ((previousModelId.current !== values.modelId || isNil(values.variantSuiteId)) && variants.length > 0) {
            previousModelId.current = values.modelId;

            const initializedVariant = inventoryDetails?.variantSuiteId ?? variants[0].value;
            const newVariantSuiteId = isNil(values.variantSuiteId) ? initializedVariant : variants[0].value;
            setFieldValue('variantSuiteId', newVariantSuiteId);
        }
    }, [inventoryDetails?.variantSuiteId, setFieldValue, values.modelId, values.variantSuiteId, variants]);

    return (
        <Space direction="vertical" style={{ width: '100%' }}>
            <StyledForm>
                <SelectField
                    {...t('giftVoucherJourney:vehicle.model', { returnObjects: true })}
                    name="modelId"
                    options={models}
                    autoSelected
                    required
                />

                <SelectField
                    {...t('giftVoucherJourney:vehicle.variant', { returnObjects: true })}
                    name="variantSuiteId"
                    options={variants}
                    autoSelected
                    required
                />

                <Row gutter={12}>
                    {stockVariants.map(stock => (
                        <StockCarCard
                            selectedStockId={selectedStockId}
                            setSelectedStockId={setSelectedStockId}
                            span={stockVariants.length > 1 ? 12 : 24}
                            stock={stock}
                            translated={translated}
                        />
                    ))}
                </Row>
            </StyledForm>
        </Space>
    );
};

const ChangeVehicleDrawer = ({ moduleId, onClose, title, visible, translated, onStockChange }: ChangeVehicleProps) => {
    const { Button } = useThemeComponents();
    const { t } = useTranslation('giftVoucherJourney');
    const { values, setFieldValue } = useFormikContext<GiftVoucherKYCJourneyValues>();

    const screens = Grid.useBreakpoint();
    const drawerWidth = useMemo(() => {
        if (screens.lg) {
            return 450;
        }

        // Default
        return undefined;
    }, [screens.lg]);

    const [selectedStockId, setSelectedStockId] = useState(values.stockId);
    const { data, loading } = useListMobilityStocksQuery({
        fetchPolicy: 'cache-and-network',
        variables: {
            filter: {
                kind: InventoryKind.MobilityInventory,
                moduleId,
                productionOnly: true,
            },
            pagination: null,
            sort: null,
        },
    });

    const stocks = useMemo(() => data?.list.items || [], [data?.list.items]);

    const onCloseOnly = useCallback(
        event => {
            // Reset the selected stock id from formik values
            setSelectedStockId(values.stockId);

            // Reset formik modelId
            setFieldValue('modelId', null);
            setFieldValue('variantSuiteId', null);

            onClose(event);
        },
        [onClose, setFieldValue, values.stockId]
    );

    const applySelection = useCallback(
        event => {
            const selectedStock = stocks.find(stock => stock.id === selectedStockId);
            if (
                selectedStock?.__typename === 'MobilityStockInventory' &&
                selectedStock?.inventory?.__typename === 'MobilityInventory'
            ) {
                const stockCarPrice = selectedStock.price || selectedStock.inventory.variant.vehiclePrice;

                setFieldValue('value', stockCarPrice);
                setFieldValue('stockId', selectedStockId);

                // Set also inner state, to adjust vehicle info card from outside of the drawer
                onStockChange(selectedStock);

                onClose(event);
            }

            return null;
        },
        [stocks, selectedStockId, setFieldValue, onStockChange, onClose]
    );

    if (loading) {
        return <PortalLoadingElement />;
    }

    return (
        <StyledDrawer
            footer={
                <Button disabled={!selectedStockId} onClick={applySelection} type="secondary">
                    {t('giftVoucherJourney:journey.actions.closeDrawer')}
                </Button>
            }
            onClose={onCloseOnly}
            placement="left"
            title={title}
            visible={visible}
            width={drawerWidth}
            destroyOnClose
        >
            <VehicleSelection
                selectedStockId={selectedStockId}
                setSelectedStockId={setSelectedStockId}
                stocks={stocks}
                translated={translated}
            />
        </StyledDrawer>
    );
};

export const useChangeVehicleDrawer = (
    moduleId: string,
    isDrafted: boolean,
    onStockChange: (stock: MobilityStockPublicListFragment) => void
) => {
    const [visible, setVisible] = useState(false);
    const { t } = useTranslation('giftVoucherJourney');
    const translated = useTranslatedString();

    const actions = useMemo(
        () => ({
            open: () => setVisible(true),
            close: () => setVisible(false),
        }),
        [setVisible]
    );

    return {
        ...actions,
        render: () => (
            <ChangeVehicleDrawer
                moduleId={moduleId}
                onClose={actions.close}
                onStockChange={onStockChange}
                title={
                    <DrawerTitle>
                        {isDrafted
                            ? t('giftVoucherJourney:changeVehicleDrawer.title')
                            : t('giftVoucherJourney:selectVehicle')}
                    </DrawerTitle>
                }
                translated={translated}
                visible={visible}
            />
        ),
    };
};

export default ChangeVehicleDrawer;
